import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import Axios from "axios";
import MarkdownItLinkAttributes from "markdown-it-link-attributes";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";
import Localizer from "@Scripts/Common/Localizer";

enum DirectLineConnectionStatus {
    Uninitialized,
    Connecting,
    Connected,
    ExpiredToken,
    FailedToConnect,
    Disconnected
}

@Component({ name: "Chat" })
export default class Chat extends Vue {
    public localizer: any = Localizer;
    public token: any = null;
    public isShowChat: boolean = false;
    public isShowPreview: boolean = false;
    public refreshTimeOut: any = null;
    public isChatScriptLoad: boolean = false;

    @Prop() readonly culture: string;

    public created() {
        this.token = JSON.parse(window.localStorage.getItem("ChatToken"));
        this.isShowChat = window.localStorage.getItem("ShowChat") === "true" ? true : false;

        if (!window.localStorage.getItem("ChatPrev")) {
            this.isShowPreview = true;
        }

        if (this.isShowChat) {
            this.getChatScript();
        }
    }

    public async mounted() {
        if (this.token) {
            await this.refreshToken();
            this.renderChat();
        }
    }

    public getChatScript() {
        const self = this;
        if (!this.isChatScriptLoad) {
            let chatScript = document.createElement("script");
            chatScript.setAttribute("src", "https://cdn.botframework.com/botframework-webchat/latest/webchat.js");
            document.head.appendChild(chatScript);

            chatScript.addEventListener("load", function () {
                self.isChatScriptLoad = true;
                self.openChat();
            });
        }
        else {
            this.openChat();
        }
    }

    public async closePreview() {
        this.isShowPreview = false;
        window.localStorage.setItem("ChatPrev", "false");
        GTagHelper.sendCustomEvent("webchat", { webchat_action: "close_preview" });
    }

    public async openChat() {
        GTagHelper.sendCustomEvent("webchat", { webchat_action: "open" });

        if (!this.token) {
            await this.getToken();
        } else {
            await this.refreshToken();
        }

        await this.renderChat();

        window.localStorage.setItem("ShowChat", "true");
        window.localStorage.setItem("ChatPrev", "false");
        this.isShowChat = true;
        this.isShowPreview = false;
    }

    public async closeChat() {
        GTagHelper.sendCustomEvent("webchat", { webchat_action: "close" });
        window.localStorage.setItem("ShowChat", "false");
        this.isShowChat = false;
        this.isShowPreview = false;
    }

    public async getToken() {
        const response = (await Axios.post((<any>window).App.routes.Chat_GetToken)).data;
        window.localStorage.setItem("ChatToken", JSON.stringify(response));
        this.token = Object.freeze(response);

        this.debounceResreshToken();
    }

    public async debounceResreshToken() {
        let self = this;

        clearTimeout(this.refreshTimeOut);
        this.refreshTimeOut = setTimeout(async function () {
            await self.refreshToken();
        }, (this.token.expires_In - 60) * 1000, this);
    }

    public async refreshToken() {
        const response = (await Axios.post((<any>window).App.routes.Chat_RefreshToken, { token: this.token.token })).data;
        if (response?.token) {
            window.localStorage.setItem("ChatToken", JSON.stringify(response));
            this.token = Object.freeze(response);
        } else {
            await this.getToken();
        }
    }

    public async renderChat() {
        const markdownIt = (<any>window).MarkdownIt({ linkify: true });
        markdownIt.use(MarkdownItLinkAttributes, { target: "_self" });

        if ((<any>window).WebChat) {
            // Intercept chat events
            const store = (<any>window).WebChat.createStore({}, ({ dispatch }) => next => action => {
                switch (action.type) {
                    case "DIRECT_LINE/CONNECT_FULFILLED":
                        dispatch({
                            type: "WEB_CHAT/SEND_EVENT",
                            payload: {
                                name: "webchat/join",
                                value: { language: this.culture }
                            }
                        });
                        break;
                    case "DIRECT_LINE/UPDATE_CONNECTION_STATUS":
                        switch (action.payload.connectionStatus) {
                            case DirectLineConnectionStatus.Connected:
                                GTagHelper.sendCustomEvent("webchat", { webchat_action: "start" });
                                break;
                            case DirectLineConnectionStatus.FailedToConnect:
                                GTagHelper.sendCustomEvent("webchat", { webchat_action: "failed_connect" });
                                break;
                            case DirectLineConnectionStatus.Disconnected:
                                GTagHelper.sendCustomEvent("webchat", { webchat_action: "end" });
                                break;
                        }
                        break;
                    case "WEB_CHAT/SEND_POST_BACK":
                        if (action.payload.value != null) {
                            GTagHelper.sendCustomEvent("webchat", { webchat_message: action.payload.value });
                        }
                        break;
                    case "WEB_CHAT/SEND_MESSAGE":
                        if (action.payload.method === "imBack" && action.payload.text.length === 1) {
                            GTagHelper.sendCustomEvent("webchat", { webchat_feedback: action.payload.text });
                        } else {
                            GTagHelper.sendCustomEvent("webchat", { webchat_message: "send_message" });
                        }
                        break;
                }

                return next(action);
            });

            (<any>window).WebChat.renderWebChat({
                directLine: (<any>window).WebChat.createDirectLine({
                    token: this.token.token
                }),
                store,
                userID: this.token.conversationId,
                username: "Guest",
                locale: this.culture,
                webSocket: true,
                styleOptions: {
                    hideUploadButton: true
                },
                renderMarkdown: text => markdownIt.render(text)
            }, document.getElementById("webchat"));
        }

        // Add click listener to href items inside chat
        document.getElementById("webchat").addEventListener("click", (element: any) => {
            var url = element.target.getAttribute("href");
            if (url) {
                GTagHelper.sendCustomEvent("webchat", { webchat_action: "click" });
            }
        });
    }
}